/**
 * laneItemStructure — recover the STRUCTURE trapped inside lane-item prose.
 *
 * The cross-domain lanes render generator output that is secretly tabular:
 *
 *   title:  "Land Availability: Available land: 1170 acres (95/100)"
 *   title:  "Grid capacity"
 *   detail: "Constrained is a material siting constraint requiring explicit
 *            mitigation pathway."
 *   title:  "Condition 1"
 *   detail: "CRITICAL: Flood Risk, Flood risk: High (score 20)"
 *
 * Reading it as sentences buries the fact (the VALUE) mid-line and repeats
 * the same boilerplate on every negative row. This module parses ONLY the
 * three shapes above — anything else stays narrative and renders as a card.
 * Guessing structure that isn't there would be worse than prose, so every
 * matcher is anchored and total misses return null.
 *
 * Pure module — no React. Renderers own presentation.
 */

export interface StructuredLaneFact {
  /** What is being measured — "Grid capacity", "Land Availability". */
  metric: string;
  /** The fact itself — "Constrained", "1170 acres", "24-48 months". */
  value: string;
  /** 0–100 when the item carries an explicit score, else null. */
  score: number | null;
  /** True when the value came from the shared boilerplate sentence, so the
   *  renderer can collapse that sentence into one lane-level caption. */
  fromBoilerplate: boolean;
}

/** The repeated generator templates on negative findings. The long sentence
 *  is the LEGACY form and persists in already-generated reports; the compact
 *  parenthetical is what buildSuggestedBullet emits from 2026-08-03. */
const BOILERPLATE_RE =
  /^(.+?) is a material siting constraint requiring explicit mitigation pathway\.?$/;
const BOILERPLATE_COMPACT_RE = /^(.+?)\s*\(mitigation pathway required\)$/;

/** "Label: Sub-label: value (NN/100)" — scored metric packed into a title. */
const SCORED_TITLE_RE = /^([^:]+):\s*[^:]+:\s*(.+?)\s*\((\d{1,3})\/100\)$/;

/** "... (score NN)" — conditions carry their score inline. */
const CONDITION_SCORE_RE = /\(score\s+(\d{1,3})\)\s*$/i;

export function parseLaneFact(
  title: string,
  detail: string | undefined,
): StructuredLaneFact | null {
  const t = title.trim();
  const d = (detail ?? '').trim();

  const scored = SCORED_TITLE_RE.exec(t);
  if (scored) {
    const score = Number(scored[3]);
    if (Number.isFinite(score) && score >= 0 && score <= 100) {
      return {
        metric: scored[1].trim(),
        value: scored[2].trim(),
        score,
        fromBoilerplate: false,
      };
    }
  }

  const boiler = BOILERPLATE_RE.exec(d) ?? BOILERPLATE_COMPACT_RE.exec(d);
  if (boiler && t.length > 0 && !t.includes(':')) {
    return {
      metric: t,
      value: boiler[1].trim(),
      score: null,
      fromBoilerplate: true,
    };
  }

  return null;
}

/** Conditions: "…, Flood risk: High (score 20)" → label + score bar. */
export function parseConditionScore(
  title: string,
  detail: string | undefined,
): { label: string; score: number } | null {
  const d = (detail ?? '').trim();
  const m = CONDITION_SCORE_RE.exec(d);
  if (!m) return null;
  const score = Number(m[1]);
  if (!Number.isFinite(score) || score < 0 || score > 100) return null;
  const label = d.replace(CONDITION_SCORE_RE, '').replace(/[,:\s]+$/, '').trim();
  return { label: label.length > 0 ? label : title.trim(), score };
}

/**
 * Value-first rewrite of a scored-formula TITLE for prose surfaces (report
 * reader, DOCX). 'Land Availability: Available land: 1170 acres (95/100)'
 * → 'Land Availability — 1170 acres · 95/100'. Unparseable titles pass
 * through untouched, per the no-invented-structure rule.
 */
export function formatFindingTitle(title: string): string {
  const fact = parseLaneFact(title, undefined);
  if (!fact || fact.score == null) return title;
  return `${fact.metric} — ${fact.value} · ${fact.score}/100`;
}

export const BOILERPLATE_CAPTION =
  'Negative findings are material siting constraints requiring explicit mitigation.';
